import {
    useCallback,
    useEffect,
    useState
} from "react";
import {
    ActivityIndicator,
    View
} from "react-native";
import type AccountDeletionRequestsProps from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitSnackBar,
    NCoreUIKitTheme,
    TextInput,
    Sticker,
    Button,
    Dialog,
    Text
} from "ncore-ui-kit";
import type {
    AccountDeletionRequestHistoryItemType
} from "../../../../services/restAPI/actions/getAllAccountDeletionRequestHistories/type";
import type {
    AccountDeletionItemType
} from "../../../../services/restAPI/actions/getAllAccountDeletionRequests/type";
import {
    SearchIcon,
    CheckIcon,
    PhoneIcon,
    MailIcon,
    XIcon
} from "lucide-react-native";
import moment from "moment";
import RESTServiceContext from "../../../../services/restAPI/context";
import Pagination from "../../../../components/pagination";
import RejectReasonModal from "../rejectReasonModal";

const AccountDeletionRequests = ({
    refreshKey = 0
}: AccountDeletionRequestsProps) => {
    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [
        historyReasonFilter,
        setHistoryReasonFilter
    ] = useState<"" | "approved" | "rejected">("");

    const [
        isApproveDialogVisible,
        setIsApproveDialogVisible
    ] = useState<boolean>(false);

    const [
        isRejectModalVisible,
        setIsRejectModalVisible
    ] = useState<boolean>(false);

    const [
        historyTotalCount,
        setHistoryTotalCount
    ] = useState<number>(0);

    const [
        historyItems,
        setHistoryItems
    ] = useState<AccountDeletionRequestHistoryItemType[]>([]);

    const [
        actionLoading,
        setActionLoading
    ] = useState<boolean>(false);

    const [
        approveTarget,
        setApproveTarget
    ] = useState<{ id: string; source: "contactForm" | "appSelfDelete" } | null>(null);

    const [
        rejectTarget,
        setRejectTarget
    ] = useState<{ id: string; source: "contactForm" | "appSelfDelete" } | null>(null);

    const [
        historyPage,
        setHistoryPage
    ] = useState<number>(1);

    const [
        totalCount,
        setTotalCount
    ] = useState<number>(0);

    const [
        loading,
        setLoading
    ] = useState<boolean>(true);

    const [
        tabMode,
        setTabMode
    ] = useState<"pending" | "history">("pending");

    const [
        search,
        setSearch
    ] = useState<string>("");

    const [
        items,
        setItems
    ] = useState<AccountDeletionItemType[]>([]);

    const [
        page,
        setPage
    ] = useState<number>(1);

    const fetchRequests = useCallback(async () => {
        try {
            setLoading(true);
            const response = await RESTServiceContext.action("getAllAccountDeletionRequests", {
                search: search.trim() || undefined,
                page
            });

            if (response.payload) {
                const payloadItems = Array.isArray(response.payload) ? response.payload : [];
                setItems(payloadItems);
                setTotalCount(response.totalCount || payloadItems.length || 0);
            }
        } catch {
            NCoreUIKitSnackBar.open({
                title: localize("requests-action-error")
            });
        } finally {
            setLoading(false);
        }
    }, [
        localize,
        search,
        page
    ]);

    const fetchHistoryRequests = useCallback(async () => {
        try {
            setLoading(true);
            const response = await RESTServiceContext.action("getAllAccountDeletionRequestHistories", {
                reason: historyReasonFilter === "" ? "all" : historyReasonFilter,
                search: search.trim() || undefined,
                page: historyPage,
                source: "all"
            });

            if (response.payload) {
                const payloadItems = Array.isArray(response.payload) ? response.payload : [];
                setHistoryItems(payloadItems);
                setHistoryTotalCount(response.totalCount || payloadItems.length || 0);
            }
        } catch {
            NCoreUIKitSnackBar.open({
                title: localize("requests-action-error")
            });
        } finally {
            setLoading(false);
        }
    }, [
        historyReasonFilter,
        historyPage,
        localize,
        search
    ]);

    useEffect(() => {
        if(tabMode === "pending") {
            fetchRequests();
        } else {
            fetchHistoryRequests();
        }
    }, [
        fetchHistoryRequests,
        fetchRequests,
        refreshKey,
        tabMode
    ]);

    const handleOpenApprove = (id: string, source: "contactForm" | "appSelfDelete") => {
        setApproveTarget({
            source,
            id
        });
        setIsApproveDialogVisible(true);
    };

    const handleConfirmApprove = async () => {
        if(!approveTarget) {
            return;
        }

        try {
            setActionLoading(true);
            const response = await RESTServiceContext.action("approveAccountDeletionRequest", {
                source: approveTarget.source,
                id: approveTarget.id
            });

            if(!response.isError) {
                NCoreUIKitSnackBar.open({
                    title: localize("requests-action-success")
                });
                setIsApproveDialogVisible(false);
                setApproveTarget(null);
                fetchRequests();
                fetchHistoryRequests();
            } else {
                NCoreUIKitSnackBar.open({
                    title: response.message || localize("requests-action-error")
                });
            }
        } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : localize("requests-action-error");
            NCoreUIKitSnackBar.open({
                title: errorMessage
            });
        } finally {
            setActionLoading(false);
        }
    };

    const handleOpenReject = (id: string, source: "contactForm" | "appSelfDelete") => {
        setRejectTarget({
            source,
            id
        });
        setIsRejectModalVisible(true);
    };

    const handleConfirmReject = async (reason: string) => {
        if(!rejectTarget) {
            return;
        }

        try {
            setActionLoading(true);
            const response = await RESTServiceContext.action("rejectAccountDeletionRequest", {
                rejectReason: reason.trim() || undefined,
                source: rejectTarget.source,
                id: rejectTarget.id
            });

            if(!response.isError) {
                NCoreUIKitSnackBar.open({
                    title: localize("requests-action-success")
                });
                setIsRejectModalVisible(false);
                setRejectTarget(null);
                fetchRequests();
                fetchHistoryRequests();
            } else {
                NCoreUIKitSnackBar.open({
                    title: response.message || localize("requests-action-error")
                });
            }
        } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : localize("requests-action-error");
            NCoreUIKitSnackBar.open({
                title: errorMessage
            });
        } finally {
            setActionLoading(false);
        }
    };

    const renderCard = (item: AccountDeletionItemType) => {
        const isApp = item.source === "appSelfDelete";

        return <View
            style={[
                stylesheet.cardContainer,
                {
                    backgroundColor: colors.project.brandWhite,
                    borderColor: colors.content.border.subtle,
                    borderWidth: borders.seperator,
                    borderRadius: radiuses.lg,
                    padding: spaces.spacingLg,
                    gap: spaces.spacingMd
                }
            ]}
            key={item.id}
        >
            <View
                style={[
                    stylesheet.cardInfoContainer,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.cardDetailsContainer,
                        {
                            gap: spaces.spacingXs
                        }
                    ]}
                >
                    <View
                        style={[
                            stylesheet.cardTitleContainer,
                            {
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        <Text
                            customColor={colors.content.text.high}
                            style={stylesheet.fullNameText}
                            variant="titleSmallSize"
                        >
                            {item.fullName}
                        </Text>
                        <Sticker
                            title={isApp ? localize("requests-deletion-source-app") : localize("requests-deletion-source-contact")}
                            type={isApp ? "danger" : "warning"}
                            spreadBehaviour="free"
                            size="small"
                        />
                    </View>
                    <Text
                        customColor={colors.content.text.low}
                        variant="labelSmallSize"
                    >
                        {moment(item.createdAt).format("DD.MM.YYYY HH:mm")}
                    </Text>
                    <View
                        style={[
                            stylesheet.cardContactRow,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <View
                            style={[
                                stylesheet.cardContactRow,
                                {
                                    gap: spaces.spacingXs
                                }
                            ]}
                        >
                            <MailIcon
                                color={colors.content.icon.mid}
                                size={14}
                            />
                            <Text
                                customColor={colors.content.text.mid}
                                variant="bodySmallSize"
                            >
                                {item.mail}
                            </Text>
                        </View>
                        {
                            item.phoneNumber ?
                                <View
                                    style={[
                                        stylesheet.cardContactRow,
                                        {
                                            gap: spaces.spacingXs
                                        }
                                    ]}
                                >
                                    <PhoneIcon
                                        color={colors.content.icon.mid}
                                        size={14}
                                    />
                                    <Text
                                        customColor={colors.content.text.mid}
                                        variant="bodySmallSize"
                                    >
                                        {item.phoneNumber}
                                    </Text>
                                </View>
                            :
                                null
                        }
                    </View>
                    {
                        item.reason ?
                            <View
                                style={[
                                    stylesheet.reasonContainer,
                                    {
                                        backgroundColor: colors.content.container.subtle,
                                        marginTop: spaces.spacingXs,
                                        borderRadius: radiuses.sm,
                                        padding: spaces.spacingSm,
                                        gap: spaces.spacingXs
                                    }
                                ]}
                            >
                                <Text
                                    customColor={colors.content.text.low}
                                    variant="labelSmallSize"
                                >
                                    {localize("requests-deletion-reason")}
                                </Text>
                                <Text
                                    customColor={colors.content.text.high}
                                    variant="bodySmallSize"
                                >
                                    {item.reason}
                                </Text>
                            </View>
                        :
                            null
                    }
                </View>
            </View>
            <View
                style={[
                    stylesheet.cardActionsContainer,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Button
                    onPress={() => handleOpenReject(item.id, item.source)}
                    title={localize("requests-action-reject")}
                    icon={({
                        color,
                        size
                    }) => <XIcon
                        color={colors.content.icon[color]}
                        size={size}
                    />}
                    spreadBehaviour="free"
                    variant="filled"
                    size="medium"
                    type="danger"
                />
                <Button
                    onPress={() => handleOpenApprove(item.id, item.source)}
                    title={localize("requests-action-approve")}
                    icon={({
                        color,
                        size
                    }) => <CheckIcon
                        color={colors.content.icon[color]}
                        size={size}
                    />}
                    spreadBehaviour="free"
                    variant="filled"
                    type="success"
                    size="medium"
                />
            </View>
        </View>;
    };

    const renderHistoryCard = (item: AccountDeletionRequestHistoryItemType) => {
        const isApp = item.source === "appSelfDelete";
        const isApproved = item.reason === "approved";

        return <View
            style={[
                stylesheet.cardContainer,
                {
                    backgroundColor: colors.project.brandWhite,
                    borderColor: colors.content.border.subtle,
                    borderWidth: borders.seperator,
                    borderRadius: radiuses.lg,
                    padding: spaces.spacingLg,
                    gap: spaces.spacingMd
                }
            ]}
            key={item.historyID}
        >
            <View
                style={[
                    stylesheet.cardInfoContainer,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.cardDetailsContainer,
                        {
                            gap: spaces.spacingXs
                        }
                    ]}
                >
                    <View
                        style={[
                            stylesheet.cardTitleContainer,
                            {
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        <Text
                            customColor={colors.content.text.high}
                            style={stylesheet.fullNameText}
                            variant="titleSmallSize"
                        >
                            {item.fullName || "-"}
                        </Text>
                        <Sticker
                            title={isApp ? localize("requests-deletion-source-app") : localize("requests-deletion-source-contact")}
                            type={isApp ? "danger" : "warning"}
                            spreadBehaviour="free"
                            size="small"
                        />
                    </View>
                    <Text
                        customColor={colors.content.text.low}
                        variant="labelSmallSize"
                    >
                        {moment(item.createdAt).format("DD.MM.YYYY HH:mm")}
                    </Text>
                    <View
                        style={[
                            stylesheet.cardContactRow,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        {
                            item.mail ?
                                <View
                                    style={[
                                        stylesheet.cardContactRow,
                                        {
                                            gap: spaces.spacingXs
                                        }
                                    ]}
                                >
                                    <MailIcon
                                        color={colors.content.icon.mid}
                                        size={14}
                                    />
                                    <Text
                                        customColor={colors.content.text.mid}
                                        variant="bodySmallSize"
                                    >
                                        {item.mail}
                                    </Text>
                                </View>
                            :
                                null
                        }
                        {
                            item.phoneNumber ?
                                <View
                                    style={[
                                        stylesheet.cardContactRow,
                                        {
                                            gap: spaces.spacingXs
                                        }
                                    ]}
                                >
                                    <PhoneIcon
                                        color={colors.content.icon.mid}
                                        size={14}
                                    />
                                    <Text
                                        customColor={colors.content.text.mid}
                                        variant="bodySmallSize"
                                    >
                                        {item.phoneNumber}
                                    </Text>
                                </View>
                            :
                                null
                        }
                    </View>
                    {
                        item.note ?
                            <View
                                style={[
                                    stylesheet.reasonContainer,
                                    {
                                        backgroundColor: colors.content.container.subtle,
                                        marginTop: spaces.spacingXs,
                                        borderRadius: radiuses.sm,
                                        padding: spaces.spacingSm,
                                        gap: spaces.spacingXs
                                    }
                                ]}
                            >
                                <Text
                                    customColor={colors.content.text.low}
                                    variant="labelSmallSize"
                                >
                                    {localize("requests-rejection-reason")}
                                </Text>
                                <Text
                                    customColor={colors.content.text.high}
                                    variant="bodySmallSize"
                                >
                                    {item.note}
                                </Text>
                            </View>
                        :
                            null
                    }
                </View>
            </View>
            <View
                style={[
                    stylesheet.cardActionsContainer,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Sticker
                    title={isApproved ? localize("requests-status-approved") : localize("requests-status-rejected")}
                    type={isApproved ? "success" : "danger"}
                    spreadBehaviour="free"
                    size="small"
                />
            </View>
        </View>;
    };

    const renderListContent = () => {
        if(loading) {
            return <View
                style={stylesheet.loadingContainer}
            >
                <ActivityIndicator
                    color={colors.content.text.high}
                    size="large"
                />
            </View>;
        }

        const isPending = tabMode === "pending";
        const currentItems = isPending ? items : historyItems;
        const currentCount = isPending ? totalCount : historyTotalCount;
        const currentPage = isPending ? page : historyPage;

        if(currentItems.length === 0) {
            return <View
                style={stylesheet.emptyContainer}
            >
                <Text
                    customColor={colors.content.text.mid}
                    variant="bodyMediumSize"
                >
                    {isPending ? localize("requests-empty-list") : localize("requests-history-empty")}
                </Text>
            </View>;
        }

        return <View
            style={[
                stylesheet.listContainer,
                {
                    gap: spaces.spacingMd
                }
            ]}
        >
            {
                isPending ?
                    items.map(renderCard)
                :
                    historyItems.map(renderHistoryCard)
            }
            {
                currentCount > 20 ?
                    <View
                        style={stylesheet.paginationContainer}
                    >
                        <Pagination
                            onSelect={(paginationItem) => {
                                if(isPending) {
                                    setPage(paginationItem.pageNumber);
                                } else {
                                    setHistoryPage(paginationItem.pageNumber);
                                }
                            }}
                            totalDataCount={currentCount}
                            currentPage={currentPage}
                            itemPerPage={20}
                        />
                    </View>
                :
                    null
            }
        </View>;
    };

    return <View
        style={[
            stylesheet.container,
            {
                gap: spaces.spacingLg
            }
        ]}
    >
        <View
            style={[
                stylesheet.toolbarContainer,
                {
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={stylesheet.searchInputContainer}
            >
                <TextInput
                    placeholder={localize("requests-search-placeholder")}
                    customColor={colors.project.brandWhite}
                    icon={({
                        size
                    }) => <SearchIcon
                        color={colors.content.icon.mid}
                        size={size}
                    />}
                    onChangeText={(val) => {
                        setSearch(val);
                        if(tabMode === "pending") {
                            setPage(1);
                        } else {
                            setHistoryPage(1);
                        }
                    }}
                    key="deletion-requests-search"
                    spreadBehaviour="stretch"
                    value={search}
                />
            </View>
            <View
                style={[
                    stylesheet.toolbarActionsContainer,
                    {
                        backgroundColor: colors.content.container.subtle,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingXs,
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Button
                    customTextColor={tabMode === "pending" ? colors.project.brandWhite : colors.content.text.mid}
                    customColor={tabMode === "pending" ? "gold" : undefined}
                    variant={tabMode === "pending" ? "filled" : "ghost"}
                    title={localize("requests-filter-pending")}
                    onPress={() => {
                        setTabMode("pending");
                    }}
                    spreadBehaviour="free"
                    type="neutral"
                />
                <Button
                    customTextColor={tabMode === "history" ? colors.project.brandWhite : colors.content.text.mid}
                    customColor={tabMode === "history" ? "gold" : undefined}
                    variant={tabMode === "history" ? "filled" : "ghost"}
                    title={localize("requests-filter-history")}
                    onPress={() => {
                        setTabMode("history");
                    }}
                    spreadBehaviour="free"
                    type="neutral"
                />
            </View>
        </View>
        {
            tabMode === "history" ?
                <View
                    style={[
                        stylesheet.historyFilterContainer,
                        {
                            backgroundColor: colors.content.container.subtle,
                            borderRadius: radiuses.md,
                            padding: spaces.spacingXs,
                            gap: spaces.spacingXs
                        }
                    ]}
                >
                    <Button
                        customTextColor={historyReasonFilter === "" ? colors.project.brandWhite : colors.content.text.mid}
                        customColor={historyReasonFilter === "" ? "gold" : undefined}
                        variant={historyReasonFilter === "" ? "filled" : "ghost"}
                        title={localize("requests-filter-all")}
                        onPress={() => {
                            setHistoryReasonFilter("");
                            setHistoryPage(1);
                        }}
                        spreadBehaviour="free"
                        type="neutral"
                        size="small"
                    />
                    <Button
                        customTextColor={historyReasonFilter === "approved" ? colors.project.brandWhite : colors.content.text.mid}
                        customColor={historyReasonFilter === "approved" ? "gold" : undefined}
                        variant={historyReasonFilter === "approved" ? "filled" : "ghost"}
                        title={localize("requests-filter-approved")}
                        onPress={() => {
                            setHistoryReasonFilter("approved");
                            setHistoryPage(1);
                        }}
                        spreadBehaviour="free"
                        type="neutral"
                        size="small"
                    />
                    <Button
                        customTextColor={historyReasonFilter === "rejected" ? colors.project.brandWhite : colors.content.text.mid}
                        customColor={historyReasonFilter === "rejected" ? "gold" : undefined}
                        variant={historyReasonFilter === "rejected" ? "filled" : "ghost"}
                        title={localize("requests-filter-rejected")}
                        onPress={() => {
                            setHistoryReasonFilter("rejected");
                            setHistoryPage(1);
                        }}
                        spreadBehaviour="free"
                        type="neutral"
                        size="small"
                    />
                </View>
            :
                null
        }
        {renderListContent()}
        <RejectReasonModal
            onClose={() => {
                setRejectTarget(null);
                setIsRejectModalVisible(false);
            }}
            isVisible={isRejectModalVisible}
            onConfirm={handleConfirmReject}
            isLoading={actionLoading}
        />
        <Dialog
            primaryButtonProps={{
                title: localize("requests-approve-confirm-title"),
                onPress: handleConfirmApprove
            }}
            content={localize("requests-approve-confirm-desc")}
            secondaryButtonProps={{
                onPress: () => {
                    setIsApproveDialogVisible(false);
                    setApproveTarget(null);
                },
                title: localize("cancel")
            }}
            onOverlayPress={() => {
                setIsApproveDialogVisible(false);
                setApproveTarget(null);
            }}
            onClosed={() => {
                setIsApproveDialogVisible(false);
                setApproveTarget(null);
            }}
            isVisible={isApproveDialogVisible}
            variant="yes-no"
        />
    </View>;
};
export default AccountDeletionRequests;
